// React
import React, { useEffect, useState, useCallback, startTransition, useMemo, useRef} from 'react';

// Routing
import { useNavigate } from 'react-router-dom';

// MUI Components
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Autocomplete, Pagination, IconButton, MenuItem} from '@mui/material';

// MUI Icons
import ClearIcon from '@mui/icons-material/Clear';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

// Custom Hooks / Providers
import { useClinic } from '../../../components/ClinicProvider';

// React Query
import { useQuery } from '@tanstack/react-query';

// Services & Utils
import { fetchClinicalCheckedInPatients } from '../../../services/apiService';
import { formatDateYMD, formatDateDMY } from '../../../utils/dateUtils';

// Styles & Components
import { styles } from './PharmacyPatientSelector.styles';
import NoDataRow from '../../../components/common/NoDataRow';
import TableLoader from '../../../components/common/TableLoader';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

// Column config (kept simple) 
const columns = [
  { label: 'Visit Date', key: 'date' },
  { label: 'Patient', key: 'patient' },
  { label: 'Contact Number', key: 'contact' },
  { label: 'Treating Doctor', key: 'doctor' },
];

const TableHeader: React.FC = () => (
  <TableHead>
    <TableRow sx={styles.headerRow}>
      {columns.map(col => (
        <TableCell key={col.key} sx={styles.headerCell}>{col.label}</TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const PatientTableRow: React.FC<{ row: PatientRow; onClick: (row: PatientRow) => void }> = ({ row, onClick }) => (
  <TableRow
    key={row.id}
    hover
    tabIndex={0}
    sx={styles.interactiveRow}
    onClick={() => onClick(row)}
    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick(row); }}
    aria-label={`Create invoice for ${row.patient.replace(/\n.*/, '')}`}
  >
    <TableCell sx={styles.rowCell}>{row.date ? formatDateDMY(row.date) : '-'}</TableCell>
    <TableCell sx={{ ...styles.rowCell, whiteSpace: 'pre-line' }}>{row.patient}</TableCell>
    <TableCell sx={styles.rowCell}>{row.contact}</TableCell>
    <TableCell sx={styles.rowCell}>{row.doctor}</TableCell>
  </TableRow>
);

// ...existing code...

// PatientRow + transform helper (moved back from hook)
interface PatientRow {
  id: number;
  date: string;
  patient: string;
  contact: string;
  doctor: string;
}
const transformClinicalPatientToRow = (item: { id: number; appointment_date?: string; first_name: string; last_name?: string; medical_record_no?: string; gender__name_label?: string | null; mobile_number?: string; appointment_user?: Array<{ first_name: string; last_name?: string }>; }): PatientRow => ({
  id: item.id,
  date: item.appointment_date || '',
  patient: `${item.first_name} ${item.last_name || ''}\n${item.medical_record_no || ''} | ${item.gender__name_label || ''}`.trim(),
  contact: item.mobile_number || '',
  doctor: item.appointment_user && item.appointment_user.length > 0
    ? `${item.appointment_user[0].first_name} ${item.appointment_user[0].last_name || ''}`.trim()
    : '',
});

const PharmacyPatientSelector = () => {
  // Search & pagination state.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const { ClinicId } = useClinic();
  const navigate = useNavigate();

  // Debounce user input
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(h);
  }, [search]);

  // REPLACE external hook with inline useQuery
  const queryEnabled = !!ClinicId && (debouncedSearch.length === 0 || debouncedSearch.length >= 2);
  const patientsQuery = useQuery({
    queryKey: ['checkedInPatients', ClinicId, page, debouncedSearch, pageSize],
    enabled: queryEnabled,
    staleTime: 30000,
    gcTime: 300000,
    queryFn: () => {
      const offset = pageSize * (page - 1);
      const today = new Date();
      const from = new Date(today);
      from.setDate(today.getDate() - 60);
      const params = new URLSearchParams({
        checked_in: 'true',
        patient_type: 'patient',
        checked_in_date_after: formatDateYMD(from),
        checked_in_date_before: formatDateYMD(today),
        limit: String(pageSize),
        offset: String(offset),
      });
      if (debouncedSearch) params.set('search', debouncedSearch); // keep search even if no option
      return fetchClinicalCheckedInPatients(params.toString(), String(ClinicId))
        .then(res => ({
          rows: res.objects.map(transformClinicalPatientToRow),
          totalCount: res.meta?.total_count || 0,
        }));
    },
  });


  // Stable memoized rows to avoid identity change each render.
  const patientRows = useMemo(
    () => patientsQuery.data?.rows ?? [],
    [patientsQuery.data?.rows]
  );
  const totalCount = patientsQuery.data?.totalCount || 0;

  const searchingInFlight =
    patientsQuery.isFetching &&
    prevDebouncedSearchRef.current !== debouncedSearch &&
    debouncedSearch.length >= 2;

  const displayedRows = useMemo(() => {
    if (debouncedSearch && patientRows.length > 0) {
      const s = debouncedSearch.toLowerCase();
      return patientRows.filter(r => r.patient.toLowerCase().includes(s));
    }
    return patientRows;
  }, [debouncedSearch, patientRows]);

  const pagination = useMemo(() => {
    const pageCount = Math.ceil(totalCount / pageSize);
    const safePageCount = Math.max(1, pageCount);
    const currentOffset = pageSize * (page - 1);
    const showingStart = totalCount === 0 ? 0 : currentOffset + 1;
    const showingEnd = totalCount === 0 ? 0 : Math.min(currentOffset + patientRows.length, totalCount);
    return { pageCount, safePageCount, showingStart, showingEnd };
  }, [totalCount, page, patientRows.length, pageSize]);


  const handleSearchChange = useCallback((_: React.SyntheticEvent, value: string, reason?: string) => {
    if (reason === 'clear') {
      // optional: keep previous search instead of clearing
      // return;
    }
    startTransition(() => {
      setSearch(value);
      setPage(1);
    });
  }, []);

  const handleRowClick = useCallback((row: PatientRow) => {
    navigate(`/admin/billing/pharmacy/new/${row.id}`);
  }, [navigate]); 

  const getOptionLabel = useCallback((option: string | PatientRow) => {
    if (typeof option === 'string') return option;
    return option.patient ? option.patient.replace(/\n.*/, '') : '';
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
  }, []);

  const tableLoading = patientsQuery.isFetching && prevDebouncedSearchRef.current !== debouncedSearch;

  return (
    <Box>
      <Box>
        {/* Header */}
        <Box sx={styles.headerBar}>
          <IconButton size="small" sx={{ mr: 2 }} onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" sx={styles.title}>
            Create New Pharmacy Bill
          </Typography>

          <Box sx={styles.searchWrap}>
            <Autocomplete
              freeSolo
              disableClearable
              filterOptions={(options) => options}
              options={displayedRows}
              getOptionLabel={getOptionLabel}
              inputValue={search}
              onInputChange={handleSearchChange}
              onClose={() => {
                // Do nothing; prevent Autocomplete from mutating input on blur
              }}
              sx={{ width: '100%' }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search by patient"
                  size="small"
                  variant="outlined"
                  slotProps={{
                    inputLabel: { shrink: !!search },
                    input: {
                      'aria-label': 'Search by patient',
                      endAdornment: (
                        <>
                          {search && (
                            <IconButton
                              size="small"
                              aria-label="clear search"
                              onClick={handleClearSearch}
                              edge="end"
                              sx={{ p: 0.5, mr: 0.5 }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {/* Table */}
        <TableContainer sx={{ ...styles.tableContainer, position: 'relative' }}>
          {tableLoading && <TableLoader minHeight={120} size={32} thickness={3} message="Loading..." />}
          <Table size="small" aria-label="new invoice table" sx={styles.table}>
            <TableHeader />
            <TableBody>
              {(searchingInFlight ? [] : displayedRows).length > 0
                ? (searchingInFlight ? [] : displayedRows).map(row => (
                  <PatientTableRow key={row.id} row={row} onClick={handleRowClick} />
                ))
                : <NoDataRow message="No checked-in patients found." colSpan={4} />}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Footer: Pagination and entries info */}
        <Box sx={styles.footerBar}>
          <Typography variant="body2" sx={styles.footerText}>
            Showing {pagination.showingStart} to {pagination.showingEnd} of {totalCount} entries
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              select
              size="small"
              label="Rows"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1); // Reset to first page when page size changes
              }}
              sx={{ width: 92 }}
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <Pagination
              count={pagination.safePageCount}
              page={page}
              onChange={(_, p) => setPage(p)}
              shape="rounded"
              color="primary"
              size="small"
              disabled={pagination.pageCount <= 1}
              sx={styles.pagination}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
 
export default PharmacyPatientSelector;
